import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import {
  DIGITAL_PRODUCTS_FREE_PROCESS_NAME,
  getSupportedProcessesInfo,
  INQUIRY_PROCESS_NAME,
  isBookingProcess,
  isProcessWithPayment,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { displayDeliveryPickup, displayDeliveryShipping } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { userDisplayNameAsString } from '../../util/data';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_ITEM,
  LINE_ITEM_NIGHT,
  LISTING_STATE_CLOSED,
  propTypes,
  STOCK_INFINITE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  STOCK_MULTIPLE_ITEMS,
} from '../../util/types';
import { parse, stringify } from '../../util/urlHelpers';

import { ResponsiveImage, NamedLink } from '../../components';
import { isTrashedListing } from '../MyListingsSection/MyListingsSection.helpers';

import CloseIcon from '@mui/icons-material/Close';
import { DialogContent, IconButton } from '@mui/material';
import { sendTrackingEvent, TRACKING_EVENTS } from '../../analytics/trackingEvent';
import { InlineAlert } from '../../containers/EditListingPage/EditListingWizard/components/InlineAlert';
import { ListingDateRange } from '../../containers/ListingPage/ListingDetails/ListingDetails';
import { isDigitalProductListingType } from '../../util/soulwise-marketplace/listingType';
import { formatDuration } from '../../util/dates';
import { IMAGE_VARIANTS } from '../../util/images';
import { getPriceFromListing } from '../../util/price';
import { autoPickTier, isTierEligible, normalizePriceTiers } from '../../util/priceTiers';
import { prefetchCheckoutChunk, warmStripeWhenReady } from '../../util/stripeWarmup';
import {
  keepSelectedTierDuringUnitsRefresh,
  listingBookingCtaDisabled,
} from '../../util/listingBookingCta';
import { usePriceTierUnits } from '../../util/usePriceTierUnits';
import { SWDialog } from '../mui/Dialog';
import { PrimaryButton } from '../mui/Button';
import css from './OrderPanel.module.css';
import { SubmitBookingPanel } from './SubmitBookingPanel/SubmitBookingPanel';
import { BookedNote, DigitalPurchasedNote } from './SubmitBookingPanel/BookedNote';
import {
  DIGITAL_ACCESS_NONE,
  EMPTY_DIGITAL_LISTING_ACCESS,
  canViewDigitalListingContent,
  getDigitalListingCtaMessageId,
} from '../../util/digitalListingAccess';
import {
  getLowestSubscriptionPlan,
  isContentSubscriptionListing,
  listingHasTrial,
  subscriptionIntervalMessageId,
} from '../../util/digitalSubscription';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

const priceData = (price, currency, intl) => {
  // Multi-currency: format any Money with a currency code (matches ListingCard / util/price.js).
  if (price && price.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  sendTrackingEvent({ name: TRACKING_EVENTS.OPEN_ORDER_MODAL });
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const { listing, config, intl, showCurrencyMismatch = false, priceTierContext } = props;
  const duration = listing?.attributes?.publicData?.duration;
  const isSubscription = isContentSubscriptionListing(listing);
  const lowestPlan = isSubscription ? getLowestSubscriptionPlan(listing) : null;
  const intervalLabel =
    isSubscription && lowestPlan
      ? intl.formatMessage({ id: subscriptionIntervalMessageId(lowestPlan.interval) })
      : null;

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle, unitType } = getPriceFromListing(
    listing,
    config,
    intl,
    priceTierContext
  );
  const priceLabel = formattedPrice;
  if (!priceLabel) {
    return null;
  }
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA} data-testid="order-panel-cta-price">
      <div className={css.priceValue} title={priceTitle} data-testid="order-panel-cta-price-amount">
        {priceLabel}
      </div>
      {intervalLabel ? (
        <div className={css.perUnitInCTA} data-testid="order-panel-cta-interval">
          <FormattedMessage
            id="OrderPanel.priceIntervalSuffix"
            values={{ interval: intervalLabel }}
          />
        </div>
      ) : null}
    </div>
  ) : (
    <div className={css.priceContainer}>
      <span className={css.price}>{priceLabel}</span>
      {intervalLabel ? (
        <span className={css.perUnit} data-testid="order-panel-desktop-interval">
          <FormattedMessage
            id="OrderPanel.priceIntervalSuffix"
            values={{ interval: intervalLabel }}
          />
        </span>
      ) : null}
      {duration && (
        <span>
          {intl.formatMessage(
            { id: 'OrderPanel.durationSession' },
            { duration: formatDuration({ minutes: duration, intl }) }
          )}
        </span>
      )}
      {/*Hide unit*/}
      {/*<div className={css.perUnit}>*/}
      {/*  <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />*/}
      {/*</div>*/}
    </div>
  );
};

const PrePaymentOrderNoteMaybe = ({ bookingInfo }) =>
  bookingInfo?.isPayLaterListing &&
  !bookingInfo.isBookingInfoFetchInProgress &&
  !bookingInfo.isBookingViaSoulwiseNotAvailable ? (
    <InlineAlert>
      <FormattedMessage id="OrderPanel.payLaterNote" />
    </InlineAlert>
  ) : null;

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    config,
    timeslotStart,
    timeslotEnd,
    hasTimeslots,
    fetchFirstTimeslotInProgress,
    bookingInfo,
    fetchUserTransactionsInProgress,
    fetchUserTransactionsError,
    cancelCurrentBookingInProgress,
    cancelCurrentBookingError,
    onCancelCurrentBooking,
    onFetchUserTransactions,
    instantOrderError,
    digitalListingAccess,
    desktopPackagePicker,
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  const priceTiersCatalog = isContentSubscriptionListing(listing)
    ? null
    : normalizePriceTiers(publicData.priceTiers);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [scheduleStart, setScheduleStart] = useState(null);
  const handleScheduleStart = next => {
    setScheduleStart(current => {
      if (!next && current) {
        // Hidden desktop form must not clobber a mobile date/time pick.
        return current;
      }
      if (current && next && current.getTime() === next.getTime()) {
        return current;
      }
      return next;
    });
  };
  const bookingStart = scheduleStart || timeslotStart || null;
  const { unitsSoldByTierId, unitsStatus, unitsAvailabilityKnown } = usePriceTierUnits({
    listingId: listing?.id?.uuid,
    slotStart: bookingStart,
    enabled: !!priceTiersCatalog,
  });

  const prefetchCheckoutAssets = () => {
    warmStripeWhenReady(config?.stripe?.publishableKey);
    prefetchCheckoutChunk();
  };

  useEffect(() => {
    prefetchCheckoutAssets();
  }, [config?.stripe?.publishableKey]);

  const priceTierContext = {
    now: new Date(),
    bookingStart,
    listingType: publicData.listingType,
    publishedAt: listing?.attributes?.createdAt || null,
    unitsSoldByTierId: unitsAvailabilityKnown ? unitsSoldByTierId : {},
    selectedTierId,
  };

  useEffect(() => {
    if (!priceTiersCatalog) {
      setSelectedTierId(null);
      return;
    }
    const picked = autoPickTier(priceTiersCatalog.items, priceTierContext);
    setSelectedTierId(current => {
      const currentTier = priceTiersCatalog.items.find(item => item.id === current);
      return keepSelectedTierDuringUnitsRefresh({
        currentTierId: current,
        currentTierEligible: !!(currentTier && isTierEligible(currentTier, priceTierContext)),
        unitsAvailabilityKnown,
        pickedTierId: picked?.id,
      });
    });
  }, [
    listing?.id?.uuid,
    priceTiersCatalog?.enabled,
    timeslotStart,
    scheduleStart,
    unitsSoldByTierId,
    unitsAvailabilityKnown,
  ]);

  const hasEligibleTier = !!(
    priceTiersCatalog &&
    priceTiersCatalog.items.some(tier => isTierEligible(tier, priceTierContext))
  );
  const openWithoutUnits = !!(
    priceTiersCatalog &&
    priceTiersCatalog.items.some(tier =>
      isTierEligible(tier, { ...priceTierContext, unitsSoldByTierId: {} })
    )
  );
  const noPriceOptionsReady = listingBookingCtaDisabled({
    priceTiersCatalog,
    hasEligibleTier,
    unitsAvailabilityKnown,
    openWithoutUnits,
  });
  const bookingCtaDisabled = noPriceOptionsReady;

  const submitWithTier = values => {
    if (priceTiersCatalog && !selectedTierId) {
      return undefined;
    }
    if (!priceTiersCatalog || !selectedTierId) {
      return onSubmit(values);
    }
    return onSubmit({ ...values, tierId: selectedTierId });
  };

  const fetchLineItemsWithTier = params => {
    if (!onFetchTransactionLineItems) {
      return null;
    }
    if (!priceTiersCatalog || !selectedTierId) {
      return onFetchTransactionLineItems(params);
    }
    const orderData = { ...(params?.orderData || {}), tierId: selectedTierId };
    return onFetchTransactionLineItems({ ...params, orderData });
  };
  const { listingType, unitType, transactionProcessAlias = '', pickupEnabled, shippingEnabled } =
    publicData || {};
  const isDigitalListing = isDigitalProductListingType(listingType);
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const access = digitalListingAccess || EMPTY_DIGITAL_LISTING_ACCESS;
  const canViewOwnedContent = canViewDigitalListingContent(access, { isOwnListing });
  const digitalCtaMessageId = getDigitalListingCtaMessageId({
    isFree: processName === DIGITAL_PRODUCTS_FREE_PROCESS_NAME,
    status: canViewOwnedContent ? access.status : access.status || DIGITAL_ACCESS_NONE,
    accessMode: publicData.accessMode,
    hasTrial: listingHasTrial(publicData),
    trialEligible: access.trialEligible !== false,
  });
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const { formattedPrice } = getPriceFromListing(listing, config, intl, priceTierContext);

  const price = listing?.attributes?.price;
  const isPaymentProcess = isProcessWithPayment(processName);

  const showPriceMissing = isPaymentProcess && !price;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const showInvalidCurrency = isPaymentProcess && price?.currency !== marketplaceCurrency;
  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED || isTrashedListing(listing);
  const duration = listing?.attributes?.publicData?.duration;

  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const isDigitalProductFree = processName === DIGITAL_PRODUCTS_FREE_PROCESS_NAME;
  const hasInfiniteStock = STOCK_INFINITE_ITEMS.includes(listingTypeConfig?.stockType);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const effectiveProductStock =
    (isPurchase || isDigitalProductFree) && hasInfiniteStock && typeof currentStock !== 'number'
      ? 9999
      : currentStock;
  const isOutOfStock =
    (isPurchase || isDigitalProductFree) &&
    lineItemUnitType === LINE_ITEM_ITEM &&
    effectiveProductStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    (isPurchase || (isDigitalProductFree && lineItemUnitType === LINE_ITEM_ITEM)) &&
    typeof effectiveProductStock === 'number';

  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const showClosedListingHelpText = listing.id && isClosed;

  const submitDigitalOrder = () => {
    if (isClosed) {
      window.scrollTo(0, 0);
      return;
    }
    onSubmit({ quantity: '1', deliveryMethod: 'none' });
  };

  const handleMobileProductCta =
    isDigitalProductFree || isDigitalListing
      ? submitDigitalOrder
      : handleSubmit(isOwnListing, isClosed, showInquiryForm, onSubmit, history, location);
  const isOrderOpen = !!parse(location.search).orderOpen;

  /** When false, mobile footer CTA can open the order modal without requiring fetched timeslots (e.g. products). */
  const requiresTimeslotsForMobileCta = Boolean(showBookingTimeForm || showBookingDatesForm);

  const mobileSubmitOrAdvance = values => submitWithTier(values);

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const desktopClasses = classNames(className);
  const rootClasses = classNames(rootClassName || css.root);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  const BookingFormErrors = () => {
    if (showPriceMissing) {
      return <PriceMissing />;
    }

    // We support multiple currencies
    // if (showInvalidCurrency) {
    //   return <InvalidCurrency />;
    // }

    return null;
  };

  // Buyers choose a price option on checkout. The listing page only shows the
  // auto-picked price, plus a notice when no option can be booked.
  const priceTierNoticeMaybe =
    priceTiersCatalog && (unitsStatus === 'error' || noPriceOptionsReady) ? (
      <div data-testid="order-panel-price-tiers">
        {unitsStatus === 'error' ? (
          <p className={css.unitsUnavailable} data-testid="price-tier-units-unavailable">
            <FormattedMessage id="CheckoutPage.priceTierUnitsUnavailable" />
          </p>
        ) : null}
        {noPriceOptionsReady ? (
          <p className={css.unitsUnavailable} data-testid="price-tiers-none-available">
            <FormattedMessage id="PriceTierPicker.noneAvailable" />
          </p>
        ) : null}
      </div>
    ) : null;

  // Modal dialog
  const [scroll] = useState('paper');
  const descriptionElementRef = useRef(null);
  useEffect(() => {
    if (isOrderOpen) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [isOrderOpen]);

  {
    /*IMPORTANT - When editing order panel remember about changing both desktop and mobile versions*/
  }
  return (
    <div className={rootClasses}>
      {/*START - Desktop order panel*/}
      <div className={css.desktopWrapper} onPointerEnter={prefetchCheckoutAssets}>
        <div className={desktopClasses}>
          {titleDesktop ? <div className={css.orderPanelHeadingDesktop}>{titleDesktop}</div> : null}
          {desktopPackagePicker}
          {priceTierNoticeMaybe}
          {!desktopPackagePicker &&
          !isDigitalProductFree &&
          !isDigitalListing &&
          !noPriceOptionsReady ? (
            <div className={css.priceSection}>
              <PriceMaybe
                listing={listing}
                config={config}
                intl={intl}
                marketplaceCurrency={marketplaceCurrency}
                priceTierContext={priceTierContext}
              />
            </div>
          ) : null}

          {showBookingDatesForm ? (
            <BookingDatesForm
              className={css.bookingForm}
              formId="OrderPanelBookingDatesForm"
              lineItemUnitType={lineItemUnitType}
              onSubmit={submitWithTier}
              bookingSubmitDisabled={bookingCtaDisabled}
              price={price}
              marketplaceCurrency={marketplaceCurrency}
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              listingId={listing.id}
              isOwnListing={isOwnListing}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              timeZone={timeZone}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={fetchLineItemsWithTier}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
              onBookingStartChange={handleScheduleStart}
            />
          ) : showProductOrderForm ? (
            <ProductOrderForm
              formId="OrderPanelProductOrderForm"
              onSubmit={submitWithTier}
              price={price}
              marketplaceCurrency={marketplaceCurrency}
              currentStock={effectiveProductStock}
              allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
              pickupEnabled={pickupEnabled && displayPickup}
              shippingEnabled={shippingEnabled && displayShipping}
              displayDeliveryMethod={displayPickup || displayShipping}
              listingId={listing.id}
              listing={listing}
              isOwnListing={isOwnListing}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={fetchLineItemsWithTier}
              onContactUser={onContactUser}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
              processName={processName}
              isFreeDigitalProduct={isDigitalProductFree}
              isDigitalProductListing={isDigitalListing}
              digitalListingAccess={access}
              bookingSubmitDisabled={bookingCtaDisabled}
            />
          ) : showInquiryForm ? (
            <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={submitWithTier} />
          ) : null}

          {showBookingTimeForm && (
            <BookingTimeForm
              className={css.bookingForm}
              formId="OrderPanelBookingTimeForm"
              bookingSubmitDisabled={bookingCtaDisabled}
              lineItemUnitType={lineItemUnitType}
              onSubmit={submitWithTier}
              price={price}
              marketplaceCurrency={marketplaceCurrency}
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              listingId={listing.id}
              isOwnListing={isOwnListing}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              timeZone={timeZone}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={fetchLineItemsWithTier}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
              onBookingStartChange={handleScheduleStart}
              timeslotStart={timeslotStart}
              timeslotEnd={timeslotEnd}
              bookingInfo={bookingInfo}
              fetchUserTransactionsError={fetchUserTransactionsError}
              listing={listing}
              cancelCurrentBookingInProgress={cancelCurrentBookingInProgress}
              cancelCurrentBookingError={cancelCurrentBookingError}
              onCancelCurrentBooking={onCancelCurrentBooking}
              onFetchUserTransactions={onFetchUserTransactions}
              fetchUserTransactionsInProgress={fetchUserTransactionsInProgress}
              instantOrderError={instantOrderError}
              hasTimeslots={hasTimeslots}
              fetchFirstTimeslotInProgress={fetchFirstTimeslotInProgress}
            />
          )}
          <BookingFormErrors />
        </div>
        {/*END - Desktop order panel*/}
        <PrePaymentOrderNoteMaybe bookingInfo={bookingInfo} />
      </div>

      {/*START - Mobile order footer*/}
      <div
        className={`${css.openOrderForm} ${isDigitalListing ? css.openOrderFormDigital : ''} ${
          showBookingTimeForm || showBookingDatesForm ? css.openOrderFormBooking : ''
        } ${canViewOwnedContent ? css.openOrderFormWithNote : ''} ${
          bookingInfo.isBookingViaSoulwiseNotAvailable ? css.openOrderFormBooked : ''
        }`}
        onPointerEnter={prefetchCheckoutAssets}
      >
        {bookingInfo.isCurrentlyBooked && !bookingInfo.isBookingInfoFetchInProgress ? (
          <div className={css.mobileBookedNote}>
            <BookedNote bookingInfo={bookingInfo} listing={listing} />
          </div>
        ) : null}
        {(isDigitalListing || !isDigitalProductFree) &&
        !(canViewOwnedContent && isContentSubscriptionListing(listing)) &&
        !noPriceOptionsReady ? (
          <PriceMaybe
            listing={listing}
            config={config}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
            showCurrencyMismatch
            priceTierContext={priceTierContext}
          />
        ) : null}
        {noPriceOptionsReady ? (
          <p className={css.mobileNoneAvailable} data-testid="order-panel-mobile-none-available">
            <FormattedMessage id="PriceTierPicker.noneAvailable" />
          </p>
        ) : null}
        <div className={css.openOrderFormButtonContainer}>
          {showProductOrderForm ? (
            canViewOwnedContent ? (
              <NamedLink
                name="ContentLibraryDetailPage"
                params={{ transactionId: access.transactionId }}
                className={css.digitalViewLink}
              >
                <PrimaryButton component="span" sx={{ width: '100%' }}>
                  <FormattedMessage id={digitalCtaMessageId} />
                </PrimaryButton>
              </NamedLink>
            ) : (
              <PrimaryButton onClick={handleMobileProductCta} sx={{ width: '100%' }}>
                <FormattedMessage
                  id={
                    isDigitalListing ? digitalCtaMessageId : 'OrderPanel.ctaButtonMessagePurchase'
                  }
                />
              </PrimaryButton>
            )
          ) : (
            <SubmitBookingPanel
              stickyBar
              bookingInfo={bookingInfo}
              buttonProps={{
                onClick: handleSubmit(
                  isOwnListing,
                  isClosed,
                  showInquiryForm,
                  onSubmit,
                  history,
                  location
                ),
                disabled: isOwnListing || bookingCtaDisabled,
              }}
              cancelCurrentBookingInProgress={cancelCurrentBookingInProgress}
              cancelCurrentBookingError={cancelCurrentBookingError}
              onCancelCurrentBooking={onCancelCurrentBooking}
              onFetchUserTransactions={onFetchUserTransactions}
              fetchUserTransactionsInProgress={fetchUserTransactionsInProgress}
              listing={listing}
              hasTimeslots={hasTimeslots}
              requiresTimeslots={requiresTimeslotsForMobileCta}
            />
          )}
        </div>
        {canViewOwnedContent ? (
          <DigitalPurchasedNote
            messageId={
              isContentSubscriptionListing(listing)
                ? 'OrderPanel.activeSubscriptionNote'
                : undefined
            }
          />
        ) : null}
      </div>
      {/*END - Mobile order footer*/}

      {/*START - Mobile order panel*/}
      <SWDialog
        disableEnforceFocus
        open={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        fullScreen={true}
        PaperProps={{
          sx: { minWidth: { md: 630 }, maxHeight: '100vh' },
        }}
        scroll="paper"
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
      >
        <IconButton
          aria-label="close"
          onClick={() => closeOrderModal(history, location)}
          sx={theme => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          dividers={scroll === 'paper'}
          ref={descriptionElementRef}
          tabIndex={-1}
          sx={{
            marginTop: '73px',
            borderTop: 0,
          }}
        >
          <div className={css.listingDetailsMobileContainer}>
            {!isDigitalListing ? (
              <>
                <div className={css.listingImageMobileWrapper}>
                  <ResponsiveImage
                    className={css.responsiveImageMobile}
                    image={listing?.images?.[0]}
                    alt={listing.attributes?.title}
                    variants={[IMAGE_VARIANTS.SCALED_SMALL]}
                  />
                </div>
                <div className={css.listingTitleMobileWrapper}>
                  <div className={css.listingTitleMobile}>{title}</div>
                  {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}

                  {bookingInfo.isIndividualListing ? (
                    <span>{formatDuration({ minutes: duration, intl })}</span>
                  ) : (
                    <ListingDateRange
                      className={css.listingDateRangeMobile}
                      startDate={timeslotStart}
                      endDate={timeslotEnd}
                      intl={intl}
                      hasTimeslots={hasTimeslots}
                      fetchInProgress={fetchFirstTimeslotInProgress}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className={css.listingTitleMobileWrapper}>
                <div className={css.listingTitleMobile}>{title}</div>
              </div>
            )}
          </div>

          <BookingFormErrors />
          {priceTierNoticeMaybe}
          {!isDigitalProductFree && !isDigitalListing && formattedPrice ? (
            <div className={css.mobilePriceContainer}>
              <div className={css.mobilePriceTitle}>
                <FormattedMessage id="OrderPanel.price" />
              </div>
              <div className={css.mobileTotalPrice}>{formattedPrice}</div>
            </div>
          ) : null}

          <>
            {showBookingDatesForm ? (
              <BookingDatesForm
                className={css.bookingForm}
                formId="OrderPanelBookingDatesFormMobile"
                bookingSubmitDisabled={bookingCtaDisabled}
                lineItemUnitType={lineItemUnitType}
                onSubmit={mobileSubmitOrAdvance}
                price={price}
                marketplaceCurrency={marketplaceCurrency}
                dayCountAvailableForBooking={dayCountAvailableForBooking}
                listingId={listing.id}
                isOwnListing={isOwnListing}
                monthlyTimeSlots={monthlyTimeSlots}
                onFetchTimeSlots={onFetchTimeSlots}
                timeZone={timeZone}
                marketplaceName={marketplaceName}
                onFetchTransactionLineItems={fetchLineItemsWithTier}
                lineItems={lineItems}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchLineItemsError={fetchLineItemsError}
                payoutDetailsWarning={payoutDetailsWarning}
                onBookingStartChange={handleScheduleStart}
              />
            ) : null}

            {showProductOrderForm ? (
              <ProductOrderForm
                formId="OrderPanelProductOrderFormMobile"
                bookingSubmitDisabled={bookingCtaDisabled}
                onSubmit={mobileSubmitOrAdvance}
                price={price}
                marketplaceCurrency={marketplaceCurrency}
                currentStock={effectiveProductStock}
                allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
                pickupEnabled={pickupEnabled && displayPickup}
                shippingEnabled={shippingEnabled && displayShipping}
                displayDeliveryMethod={displayPickup || displayShipping}
                listingId={listing.id}
                listing={listing}
                isOwnListing={isOwnListing}
                marketplaceName={marketplaceName}
                onFetchTransactionLineItems={fetchLineItemsWithTier}
                onContactUser={onContactUser}
                lineItems={lineItems}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchLineItemsError={fetchLineItemsError}
                payoutDetailsWarning={payoutDetailsWarning}
                processName={processName}
                isFreeDigitalProduct={isDigitalProductFree}
                isDigitalProductListing={isDigitalListing}
                digitalListingAccess={access}
              />
            ) : null}

            {showInquiryForm ? (
              <InquiryWithoutPaymentForm
                formId="OrderPanelInquiryFormMobile"
                onSubmit={mobileSubmitOrAdvance}
              />
            ) : null}

            {showBookingTimeForm ? (
              <div className={css.mobileBookingTimeForm}>
                <BookingTimeForm
                  rootClassName={css.bookingFormMobileRoot}
                  className={css.bookingForm}
                  formId="OrderPanelBookingTimeFormMobile"
                  bookingSubmitDisabled={bookingCtaDisabled}
                  lineItemUnitType={lineItemUnitType}
                  onSubmit={mobileSubmitOrAdvance}
                  price={price}
                  marketplaceCurrency={marketplaceCurrency}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                  listingId={listing.id}
                  isOwnListing={isOwnListing}
                  monthlyTimeSlots={monthlyTimeSlots}
                  onFetchTimeSlots={onFetchTimeSlots}
                  startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
                  endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
                  timeZone={timeZone}
                  marketplaceName={marketplaceName}
                  onFetchTransactionLineItems={fetchLineItemsWithTier}
                  lineItems={lineItems}
                  fetchLineItemsInProgress={fetchLineItemsInProgress}
                  fetchLineItemsError={fetchLineItemsError}
                  payoutDetailsWarning={payoutDetailsWarning}
                  onBookingStartChange={handleScheduleStart}
                  timeslotStart={timeslotStart}
                  timeslotEnd={timeslotEnd}
                  bookingInfo={bookingInfo}
                  fetchUserTransactionsError={fetchUserTransactionsError}
                  listing={listing}
                  cancelCurrentBookingInProgress={cancelCurrentBookingInProgress}
                  cancelCurrentBookingError={cancelCurrentBookingError}
                  onCancelCurrentBooking={onCancelCurrentBooking}
                  onFetchUserTransactions={onFetchUserTransactions}
                  fetchUserTransactionsInProgress={fetchUserTransactionsInProgress}
                  instantOrderError={instantOrderError}
                  hasTimeslots={hasTimeslots}
                  fetchFirstTimeslotInProgress={fetchFirstTimeslotInProgress}
                />
              </div>
            ) : null}
          </>
        </DialogContent>
      </SWDialog>
      {/*END - Mobile order panel*/}
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  payoutDetailsWarning: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
  bookingInfo: {},
  digitalListingAccess: EMPTY_DIGITAL_LISTING_ACCESS,
  desktopPackagePicker: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  payoutDetailsWarning: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,
  // from useConfiguration
  config: object.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,
  bookingInfo: propTypes.bookingInfo,
  fetchUserTransactionsInProgress: bool,
  fetchUserTransactionsError: propTypes.error,
  onCancelCurrentBooking: func,
  onFetchUserTransactions: func,
  digitalListingAccess: object,
  desktopPackagePicker: node,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withRouter, injectIntl)(OrderPanel);
